/* TTS size memory v1
   The shopper picks their EU shoe size once, the site remembers it (localStorage "tts_size").
   - Collection pages: <tts-size-memory> row (snippets/tts-size-memory.liquid) - chips, "only my size"
     switch, half-size checkbox, clear. Never filters on its own: every filter change is a click.
   - Product pages: when a remembered size exists and that size is available, it is preselected
     through the theme's own variant picker (checked radio or select + a bubbling "change" event).
     A size the shopper selects on a product page is remembered.
   Public API: window.TTSSizeMemory.get() / set(size) / setHalves(bool) / clear() / parse(value)
   and a "tts:size-change" event on document. */
(function () {
  'use strict';

  if (window.TTSSizeMemory) return;

  var KEY = 'tts_size';
  var KEY_HALVES = 'tts_size_halves';
  var KEY_AVAIL = 'tts_size_avail_added';
  var MIN = 35;
  var MAX = 47;
  // EU42 / EU 42 / EU42.5 / EU42,5 / EU42 2/3 / EU 40 1/2
  /* The Romanian storefront translates the values to "UE42"; both prefixes are the same size. */
  var RE = /^\s*(?:EU|UE)\s*(\d{2})(?:[.,](\d)|\s+(\d)\/(\d))?\s*$/i;

  var scriptEl = document.currentScript;
  var strings = {
    pdpSelected: (scriptEl && scriptEl.getAttribute('data-pdp-selected')) || ''
  };

  function store(kind) {
    try {
      return kind === 'session' ? window.sessionStorage : window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function read(key, kind) {
    try {
      var s = store(kind);
      return s ? s.getItem(key) : null;
    } catch (e) {
      return null;
    }
  }

  function write(key, value, kind) {
    try {
      var s = store(kind);
      if (!s) return;
      if (value === null || value === undefined) s.removeItem(key);
      else s.setItem(key, String(value));
    } catch (e) {
      /* private mode or blocked storage: the feature just does not remember */
    }
  }

  // "EU42 2/3" -> { base: 42, size: "42 2/3", whole: false }
  function parse(value) {
    var m = RE.exec(String(value == null ? '' : value));
    if (!m) return null;
    var base = parseInt(m[1], 10);
    var frac = '';
    if (m[2]) frac = '.' + m[2];
    else if (m[3]) frac = ' ' + m[3] + '/' + m[4];
    return { base: base, size: String(base) + frac, whole: frac === '' };
  }

  function parseAny(value) {
    var v = String(value == null ? '' : value);
    return parse(/^\s*(?:eu|ue)/i.test(v) ? v : 'EU' + v);
  }

  function inRange(p) {
    return !!p && p.base >= MIN && p.base <= MAX;
  }

  function emit() {
    try {
      document.dispatchEvent(new CustomEvent('tts:size-change', { detail: api.get() }));
    } catch (e) {
      /* old browsers without CustomEvent constructor */
    }
  }

  var announcer = null;
  function announce(text) {
    if (!text) return;
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.className = 'visually-hidden';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('role', 'status');
      document.body.appendChild(announcer);
    }
    announcer.textContent = '';
    window.setTimeout(function () {
      announcer.textContent = text;
    }, 60);
  }

  var api = {
    MIN: MIN,
    MAX: MAX,
    parse: parseAny,
    inRange: inRange,
    get: function () {
      var p = parseAny(read(KEY));
      if (!inRange(p)) return null;
      return { size: p.size, base: p.base, whole: p.whole, halves: read(KEY_HALVES) === '1' };
    },
    set: function (value) {
      var p = parseAny(value);
      if (!inRange(p)) return false;
      var current = api.get();
      write(KEY, p.size);
      // A half or third size (e.g. 42.5) only makes sense with the in-between sizes included.
      if (!p.whole) write(KEY_HALVES, '1');
      if (!current || current.size !== p.size) emit();
      return true;
    },
    setHalves: function (on) {
      write(KEY_HALVES, on ? '1' : '0');
      emit();
    },
    clear: function () {
      write(KEY, null);
      write(KEY_HALVES, null);
      emit();
    },
    announce: announce
  };

  window.TTSSizeMemory = api;

  /* ---------- Collection page row ---------- */

  function fill(template, size) {
    return String(template || '').replace('[size]', size);
  }

  function facetsAvailable() {
    /* facets.js declares "class FacetFiltersForm" at the top level: a global lexical binding, not window.* */
    try {
      /* global FacetFiltersForm */
      return (
        typeof FacetFiltersForm !== 'undefined' &&
        typeof FacetFiltersForm.renderPage === 'function' &&
        !!document.getElementById('product-grid') &&
        !!document.getElementById('ProductGridContainer')
      );
    } catch (e) {
      return false;
    }
  }

  /* The facets re-render replaces this row; remember which control had focus to restore it. */
  var pendingFocus = null;

  class TTSSizeRow extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.param = this.dataset.param || '';
      this.availParam = this.dataset.availParam || '';
      this.availValue = this.dataset.availValue || '';

      var raw = [];
      var json = this.querySelector('script[data-tts-size-values]');
      try {
        raw = JSON.parse(json ? json.textContent : '[]') || [];
      } catch (e) {
        raw = [];
      }

      this.values = [];
      for (var i = 0; i < raw.length; i++) {
        var p = parse(raw[i].v);
        if (!inRange(p)) continue;
        this.values.push({ v: raw[i].v, base: p.base, size: p.size, whole: p.whole, c: raw[i].c, a: !!raw[i].a });
      }

      this.activeValues = this.values.filter(function (x) {
        return x.a;
      });
      this.filtered = this.activeValues.length > 0;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onMemoryChange = this.render.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      document.addEventListener('tts:size-change', this.onMemoryChange);

      this.rememberFromActiveFilter();
      this.render();
      this.restoreFocus();
      this.refineCounts();
    }

    restoreFocus() {
      if (!pendingFocus) return;
      var sel = pendingFocus;
      pendingFocus = null;
      var el = this.querySelector(sel);
      if (el && !el.hidden && el.offsetParent !== null) el.focus({ preventScroll: true });
    }

    rememberFocus(el) {
      if (!el) return;
      if (el.classList && el.classList.contains('tts-size__chip')) pendingFocus = '.tts-size__chip[data-base="' + el.dataset.base + '"]';
      else if (el.getAttribute && el.getAttribute('data-action')) pendingFocus = '[data-action="' + el.getAttribute('data-action') + '"]';
    }

    /* Filter counts include sold-out variants. Ask the same collection for its in-stock counts once
       (Section Rendering API) and hide chips that would lead to an empty grid. */
    refineCounts() {
      var self = this;
      if (!this.availParam || !this.availValue || !this.dataset.sectionId || !window.fetch) return;
      var params = new URLSearchParams(window.location.search);
      if (params.getAll(this.availParam).indexOf(this.availValue) !== -1) return;
      params.delete(this.param);
      params.append(this.availParam, this.availValue);
      params.set('section_id', this.dataset.sectionId);
      fetch(window.location.pathname + '?' + params.toString(), { credentials: 'same-origin' })
        .then(function (r) {
          return r.ok ? r.text() : '';
        })
        .then(function (html) {
          if (!html || !self.isConnected) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var json = doc.querySelector('script[data-tts-size-values]');
          if (!json) return;
          var inStock = {};
          try {
            (JSON.parse(json.textContent) || []).forEach(function (x) {
              inStock[x.v] = x.c;
            });
          } catch (e) {
            return;
          }
          self.values.forEach(function (x) {
            x.c = inStock[x.v] || 0;
          });
          self.querySelectorAll('.tts-size__chip').forEach(function (chip) {
            var base = parseInt(chip.dataset.base, 10);
            var any = self.values.some(function (x) {
              return x.base === base && x.c > 0;
            });
            chip.hidden = !any;
          });
          self.render();
        })
        .catch(function () {});
    }

    disconnectedCallback() {
      document.removeEventListener('tts:size-change', this.onMemoryChange);
    }

    /* A size filtered in the drawer (one size, with or without its halves) counts as the shopper's size. */
    rememberFromActiveFilter() {
      if (!this.filtered) return;
      var bases = {};
      var baseCount = 0;
      this.activeValues.forEach(function (x) {
        if (!bases[x.base]) {
          bases[x.base] = true;
          baseCount++;
        }
      });
      if (baseCount !== 1) return;

      var base = this.activeValues[0].base;
      var mem = api.get();
      if (mem && mem.base === base) return;

      var whole = this.activeValues.filter(function (x) {
        return x.whole;
      })[0];
      if (whole) {
        api.set(whole.size);
      } else if (this.activeValues.length === 1) {
        api.set(this.activeValues[0].size);
      }
    }

    valuesFor(base, halves) {
      var list = this.values.filter(function (x) {
        return x.base === base && (x.c > 0 || x.a);
      });
      var exact = list.filter(function (x) {
        return x.whole;
      });
      var fractions = list.filter(function (x) {
        return !x.whole;
      });
      var out = exact.map(function (x) {
        return x.v;
      });
      if (halves || exact.length === 0) {
        fractions.forEach(function (x) {
          out.push(x.v);
        });
      }
      return out;
    }

    state(mem) {
      if (this.filtered) return 'filtered';
      if (mem && this.valuesFor(mem.base, mem.halves).length > 0) return 'saved';
      return 'empty';
    }

    render() {
      var mem = api.get();
      var st = this.state(mem);
      this.dataset.state = st;

      var pressed = null;
      this.querySelectorAll('.tts-size__chip').forEach(function (chip) {
        var on = !!mem && String(mem.base) === chip.dataset.base;
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) pressed = chip;
      });
      /* Keep the chosen chip visible inside the horizontal strip (mobile) without moving the page. */
      if (pressed && pressed.parentElement) {
        var strip = pressed.parentElement;
        if (strip.scrollWidth > strip.clientWidth) {
          strip.scrollLeft = Math.max(0, pressed.offsetLeft - (strip.clientWidth - pressed.offsetWidth) / 2);
        }
      }

      this.querySelectorAll('[data-show]').forEach(function (el) {
        var shows = (el.getAttribute('data-show') || '').split(' ');
        el.hidden = shows.indexOf(st) === -1;
      });

      var pillText = this.querySelector('[data-pill-text]');
      if (pillText) pillText.textContent = mem ? fill(this.dataset.pill, mem.size) : '';

      var halves = this.querySelector('[data-action="halves"]');
      if (halves && !this.filtered && mem) halves.checked = mem.halves;
    }

    onClick(event) {
      this.rememberFocus(event.target.closest('.tts-size__chip, [data-action]'));
      var chip = event.target.closest('.tts-size__chip');
      if (chip && this.contains(chip)) {
        this.pick(parseInt(chip.dataset.base, 10));
        return;
      }
      var btn = event.target.closest('[data-action]');
      if (!btn || !this.contains(btn) || btn.tagName === 'INPUT') return;
      var action = btn.getAttribute('data-action');
      if (action === 'apply') {
        var mem = api.get();
        if (mem) this.apply(mem.base, mem.halves);
      } else if (action === 'clear') {
        api.clear();
        announce(this.dataset.cleared);
        if (this.filtered) this.apply(null, false);
        else this.render();
      }
    }

    onChange(event) {
      var input = event.target;
      this.rememberFocus(input);
      var action = input.getAttribute && input.getAttribute('data-action');
      if (action === 'toggle') {
        var mem = api.get();
        if (input.checked && mem) this.apply(mem.base, mem.halves);
        else if (!input.checked) this.apply(null, false);
      } else if (action === 'halves') {
        api.setHalves(input.checked);
        var m = api.get();
        if (this.filtered && m) this.apply(m.base, input.checked);
      }
    }

    pick(base) {
      var mem = api.get();
      var size = String(base);
      /* Keep a remembered half size (e.g. 42.5) when its whole-number chip is tapped again. */
      if (mem && mem.base === base) size = mem.size;
      api.set(size);
      announce(fill(this.dataset.saved, size));
      var m = api.get();
      this.apply(base, m ? m.halves : false);
    }

    /* base = null removes the size filter (and the in-stock filter if this row added it). */
    apply(base, halves) {
      var params = new URLSearchParams(window.location.search);
      var param = this.param;
      if (!param) return;

      var keep = params.getAll(param).filter(function (v) {
        return !inRange(parse(v));
      });
      params.delete(param);
      keep.forEach(function (v) {
        params.append(param, v);
      });
      params.delete('page');

      if (base === null) {
        if (this.availParam && read(KEY_AVAIL, 'session') === '1') {
          params.delete(this.availParam);
        }
        write(KEY_AVAIL, null, 'session');
      } else {
        var values = this.valuesFor(base, halves);
        if (!values.length) return;
        values.forEach(function (v) {
          params.append(param, v);
        });
        /* Only in-stock sizes: without it the size filter also returns products whose size is sold out. */
        if (this.availParam && this.availValue) {
          if (params.getAll(this.availParam).indexOf(this.availValue) === -1) {
            params.append(this.availParam, this.availValue);
            write(KEY_AVAIL, '1', 'session');
          }
        }
      }

      var qs = params.toString();
      if (qs === window.location.search.replace(/^\?/, '')) return;

      if (facetsAvailable()) {
        FacetFiltersForm.renderPage(qs);
      } else {
        window.location.assign(window.location.pathname + (qs ? '?' + qs : ''));
      }
    }
  }

  if (!customElements.get('tts-size-memory')) {
    customElements.define('tts-size-memory', TTSSizeRow);
  }

  /* ---------- Product page ---------- */

  function optionGroups(pvs) {
    /* In DOM order = option position order (fieldset for button/swatch pickers, select for dropdowns). */
    var groups = [];
    pvs.querySelectorAll('fieldset, select').forEach(function (el) {
      if (el.tagName === 'SELECT') {
        groups.push({ el: el, type: 'select', inputs: Array.prototype.slice.call(el.options) });
      } else {
        var radios = el.querySelectorAll('input[type="radio"]');
        if (radios.length) groups.push({ el: el, type: 'radio', inputs: Array.prototype.slice.call(radios) });
      }
    });
    return groups;
  }

  function isShoeSizeGroup(group) {
    if (!group || !group.inputs.length) return false;
    var anyInRange = false;
    for (var i = 0; i < group.inputs.length; i++) {
      var p = parse(group.inputs[i].value);
      if (!p) return false;
      if (inRange(p)) anyInRange = true;
    }
    return anyInRange;
  }

  function readJSON(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function currentOptions(groups) {
    return groups.map(function (g) {
      if (g.type === 'select') return g.el.value;
      var checked = g.inputs.filter(function (i) {
        return i.checked;
      })[0];
      return checked ? checked.value : null;
    });
  }

  function hasVariantParam() {
    try {
      return new URLSearchParams(window.location.search).has('variant');
    } catch (e) {
      return false;
    }
  }

  function preselect() {
    var mem = api.get();
    if (!mem || hasVariantParam()) return;

    var info = document.querySelector('product-info[id^="MainProduct"]');
    var pvs = info && info.querySelector('product-variant-selects');
    if (!pvs) return;

    var groups = optionGroups(pvs);
    var idx = -1;
    for (var i = 0; i < groups.length; i++) {
      if (isShoeSizeGroup(groups[i])) {
        idx = i;
        break;
      }
    }
    if (idx === -1) return;

    var group = groups[idx];
    var target = group.inputs.filter(function (input) {
      var p = parse(input.value);
      return p && p.size === mem.size;
    })[0];
    if (!target || target.disabled || target.classList.contains('disabled')) return;
    /* Never swap to another product (combined listings put a different URL on the option). */
    if (target.dataset && target.dataset.productUrl && target.dataset.productUrl !== info.dataset.url) return;

    var isSelected = group.type === 'select' ? group.el.value === target.value : target.checked;
    if (isSelected) return;

    /* Available? Check the exact variant (other options unchanged) in the theme's variant JSON. */
    var variants = readJSON(pvs, '[data-all-variants]');
    if (Array.isArray(variants)) {
      var wanted = currentOptions(groups);
      wanted[idx] = target.value;
      var match = variants.filter(function (v) {
        return (
          v &&
          Array.isArray(v.options) &&
          v.options.every(function (o, n) {
            return wanted[n] === null || wanted[n] === undefined || o === wanted[n];
          })
        );
      })[0];
      if (!match || !match.available) return;
    }

    /* The theme focuses "#<id of the changed input>" after re-rendering the picker, which would scroll
       the page down on load. A temporary id that will not exist afterwards keeps the page still. */
    var focusTarget = target;
    var realId = focusTarget.id;
    if (realId) focusTarget.id = realId + '-tts-size';

    if (group.type === 'select') {
      group.el.value = target.value;
      group.el.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (realId) focusTarget.id = realId;

    if (strings.pdpSelected) announce(fill(strings.pdpSelected, mem.size));
  }

  /* Remember a size the shopper picks on any variant picker (product page, quick view). */
  document.addEventListener('change', function (event) {
    if (!event.isTrusted) return;
    var el = event.target;
    if (!el || !el.closest) return;
    var pvs = el.closest('product-variant-selects');
    if (!pvs) return;

    var value = null;
    if (el.tagName === 'SELECT') value = el.value;
    else if (el.tagName === 'INPUT' && el.type === 'radio' && el.checked) value = el.value;
    if (value === null) return;

    var group = optionGroups(pvs).filter(function (g) {
      return g.el === el || g.el.contains(el);
    })[0];
    if (!isShoeSizeGroup(group)) return;

    /* A crossed-out (sold-out) size is looked at, not chosen: do not remember it. */
    if (el.tagName === 'INPUT' && (el.disabled || el.classList.contains('visually-disabled') || (el.closest && el.closest('.visually-disabled')))) return;

    var p = parse(value);
    if (inRange(p)) api.set(p.size);
  });

  function whenReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }

  whenReady(function () {
    if (!document.querySelector('product-info[id^="MainProduct"] product-variant-selects')) return;
    Promise.all([customElements.whenDefined('product-variant-selects'), customElements.whenDefined('product-info')])
      .then(function () {
        window.setTimeout(preselect, 0);
      })
      .catch(function () {});
  });

  /* Another tab changed the size: refresh the rows on this page. */
  window.addEventListener('storage', function (event) {
    if (event.key === KEY || event.key === KEY_HALVES || event.key === null) emit();
  });
})();
